// src/services/standardsService.js
//
// Finds a standard, and finds every state that publishes it.
//
// WHAT THIS IS FOR
// ----------------
// Attaching an existing topic to a standard from the Topic Library. The
// Suggested Topics panel works from the backlog — standards with no topic.
// This is the other direction: you have the song already, and you have spotted
// a standard it also satisfies.
//
// WHY THE READS HAPPEN ON SEARCH, NOT ON OPEN
// -------------------------------------------
// Finding states means reading the standards documents for one subject and
// grade, around 44 of them. That is fine when somebody presses search; doing it
// when the panel renders would repeat it on every keystroke and every reopen.
//
// WHAT "ALL STATES THAT TEACH THIS" CAN HONESTLY MEAN
// ---------------------------------------------------
// Only for shared systems. NGSS and Common Core math codes are the same string
// in every state that adopted them, and normalizeCode folds the punctuation
// variants — Oklahoma's "3.ESS2.1", Wisconsin's "M.3.G.A.1", North Carolina's
// "NC.3.OA.7" — into one canonical form. For those, the set is real.
//
// State-specific numbering has no such set. Arkansas writes 3.E.1 and Georgia
// writes SS3E1 for the same idea, and nothing in the data connects them.
// isShared() below is what lets the UI say which case you are in rather than
// offering a cross-state attach that would quietly do nothing.

import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

const CCSS_MATH_DOMAINS = "OA|NBT|NF|MD|G|CC|RP|NS|EE|SP|F";

const STATE_ABBR = new Set(
  ("al ak az ar ca co ct de dc fl ga hi id il in ia ks ky la me md ma mi mn ms " +
   "mo mt ne nv nh nj nm ny nc nd oh ok or pa ri sc sd tn tx ut vt va wa wv wi wy"
  ).split(" ")
);

/**
 * Same normalization the audit and crosswalk use. Kept in step with
 * audit-standards-coverage.mjs — if one changes, change both, or a code will
 * match in the browser and not in the scripts.
 */
export function normalizeCode(raw) {
  if (raw === null || raw === undefined) return "";
  let c = String(raw).trim().toUpperCase();
  if (!c) return "";

  c = c.replace(/\s*\(MA\)\s*$/i, "");

  // NGSS middle and high school codes are "MS-LS1-1" and "HS-PS2-1", and "MS"
  // is also Mississippi. Without this guard every 6-8 science code loses its
  // prefix and stops matching.
  if (!/^(MS|HS)[.\-](PS|LS|ESS|ETS)\d/.test(c)) {
    const m = c.match(/^([A-Z]{2})[.\-_](.+)$/);
    if (m && STATE_ABBR.has(m[1].toLowerCase())) c = m[2];
  }

  c = c.replace(/\u00a0/g, " ").replace(/\s+/g, "");

  // Wisconsin prefixes Common Core math with a subject letter: M.3.G.A.1.
  // Only stripped when what follows is recognisably CCSS, so West Virginia's
  // own "M.3.7" is left alone.
  c = c.replace(
    new RegExp(`^M\\.((K|[1-8])\\.(${CCSS_MATH_DOMAINS})\\.[A-D]?\\.?\\d.*)$`),
    "$1"
  );

  // Oklahoma and Oregon write NGSS with dots: 3.ESS2.1 for 3-ESS2-1. The digit
  // after the domain is what makes this safe — Ohio's "3.ESS.1" has none, and
  // is genuinely a different, broader standard.
  c = c.replace(/^(K|[1-8])\.(PS|LS|ESS|ETS)(\d)\.(\d+)$/, "$1-$2$3-$4");
  c = c.replace(/^(MS|HS)\.(PS|LS|ESS|ETS)(\d)\.(\d+)$/, "$1-$2$3-$4");

  // Common Core cluster letter: 3.G.A.1 and 3.G.1 are the same standard.
  c = c.replace(
    new RegExp(`^(K|[1-8])\\.(${CCSS_MATH_DOMAINS})\\.[A-D]\\.(.+)$`),
    "$1.$2.$3"
  );

  return c.replace(/\.([A-Z])$/, "$1");
}

/**
 * True when a code belongs to a system several states share, so "every state
 * that teaches this" is a real set rather than a guess.
 */
export function isShared(code) {
  const c = normalizeCode(code);
  if (/^(K|[1-5])-(PS|LS|ESS|ETS)\d-\d+$/.test(c)) return true;
  if (/^MS-(PS|LS|ESS|ETS)\d-\d+$/.test(c)) return true;
  return new RegExp(`^(K|[1-8])\\.(${CCSS_MATH_DOMAINS})\\.\\d`).test(c);
}

/** Every state's standards for one subject and grade. ~44 documents. */
async function loadDocs(subject, grade) {
  const snap = await getDocs(
    query(
      collection(db, "standards"),
      where("subject", "==", subject),
      where("grade", "==", grade)
    )
  );
  return snap.docs.map((d) => d.data());
}

/**
 * Standards matching a search term, for one subject and grade.
 *
 * Grouped by normalized code so a shared standard appears once with every
 * state attached, rather than as 28 near-identical rows.
 */
export async function searchStandards(subject, grade, term, { state = "" } = {}) {
  const docs = await loadDocs(subject, grade);
  const q = String(term || "").trim().toLowerCase();
  const found = new Map();

  for (const d of docs) {
    if (state && d.state !== state) continue;
    for (const std of d.standards || []) {
      if (std.inScope === false) continue;
      if (
        q &&
        !String(std.code).toLowerCase().includes(q) &&
        !String(std.description).toLowerCase().includes(q)
      ) {
        continue;
      }
      const key = normalizeCode(std.code);
      if (!found.has(key)) {
        found.set(key, {
          code: key,
          subject,
          grade,
          description: std.description || "",
          theme: std.strand || null,
          shared: isShared(std.code),
          states: [],
        });
      }
      found.get(key).states.push({
        state: d.state,
        stateName: d.stateName || d.state.toUpperCase(),
        code: std.code,
      });
    }
  }

  return [...found.values()]
    .map((g) => ({
      ...g,
      stateCount: g.states.length,
      states: g.states.sort((a, b) => String(a.stateName).localeCompare(String(b.stateName))),
    }))
    .sort((a, b) => b.stateCount - a.stateCount || a.code.localeCompare(b.code))
    .slice(0, 60);
}
